<!--
  SeoHead组件
  负责设置页面SEO相关的头部标签
  支持从路由元数据或直接属性获取SEO数据
-->
<template>
  <div v-if="false" /><!-- 空模板，仅用于脚本逻辑 -->
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useHead } from '@unhead/vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/store/modules/app';
import type { App } from '@/types/app';

// 获取国际化及路由信息
const { t, locale } = useI18n();
const route = useRoute();
const appStore = useAppStore();

const baseUrl = import.meta.env.VITE_APP_URL || 'https://americanrestaurantalliance.com';

// 定义属性
const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  keywords: {
    type: String,
    default: '',
  },
  ogImage: {
    type: String,
    default: '',
  },
  noIndex: {
    type: Boolean,
    default: false,
  },
  // 可选的自定义规范链接
  canonicalUrl: {
    type: String,
    default: '',
  },
});

// 获取当前页面的SEO配置
const currentSeoConfig = computed(() => {
  // 从路由元数据获取SEO键
  const seoKey = route.meta?.seoKey as string;

  if (!seoKey) {
    // 如果没有指定seoKey，返回默认设置
    return {
      title: t('seo.default.title'),
      description: t('seo.default.description'),
      keywords: t('seo.default.keywords'),
      ogImage: props.ogImage || '/images/og-image.jpg',
      noIndex: props.noIndex
    };
  }

  // 从i18n中获取对应语言的SEO数据
  return {
    title: t(`seo.pages.${seoKey}.title`),
    description: t(`seo.pages.${seoKey}.description`),
    keywords: t(`seo.pages.${seoKey}.keywords`),
    ogImage: props.ogImage || `/images/og-image.jpg`,
    noIndex: props.noIndex || (seoKey.includes('500') || seoKey.includes('500') || seoKey.includes('500'))
  };
});

// 默认站点配置
const siteConfig = computed(() => ({
  siteName: t('seo.default.siteName'),
  separator: t('seo.default.separator'),
  suffix: t('seo.default.suffix')
}));

// 生成规范链接（canonical URL）
const canonicalUrl = computed(() => {
  if (props.canonicalUrl) {
    return props.canonicalUrl;
  }

  // 添加语言路径前缀，除非是默认语言（英语）
  const localePath = locale.value.startsWith('en') ? '' : `/${locale.value.split('-')[0]}`;
  return `${baseUrl}${localePath}${route.path}`;
});

// 计算完整标题（包含站点名称）
const fullTitle = computed(() => {
  // 如果标题中已经包含站点名称，则不再追加
  if (currentSeoConfig.value.title.includes(siteConfig.value.siteName)) {
    return currentSeoConfig.value.title;
  }

  return `${currentSeoConfig.value.title}${siteConfig.value.separator}${siteConfig.value.siteName}`;
});

// 设置站点地图URL
const sitemapUrl = `${baseUrl}/sitemap.xml`;

// 更新文档头部
useHead({
  title: fullTitle,
  meta: computed(() => [
    // 基本元数据
    { name: 'description', content: currentSeoConfig.value.description, key: 'description' },
    { name: 'keywords', content: currentSeoConfig.value.keywords, key: 'keywords' },

    // Open Graph 元数据
    { property: 'og:title', content: fullTitle, key: 'og:title' },
    { property: 'og:description', content: currentSeoConfig.value.description, key: 'og:description' },
    { property: 'og:type', content: 'website', key: 'og:type' },
    { property: 'og:url', content: canonicalUrl.value, key: 'og:url' },
    { property: 'og:image', content: currentSeoConfig.value.ogImage, key: 'og:image' },

    // Twitter Card 元数据
    { name: 'twitter:card', content: 'summary_large_image', key: 'twitter:card' },
    { name: 'twitter:title', content: fullTitle, key: 'twitter:title' },
    { name: 'twitter:description', content: currentSeoConfig.value.description, key: 'twitter:description' },
    { name: 'twitter:image', content: currentSeoConfig.value.ogImage, key: 'twitter:image' },

    // 语言元数据
    {
      name: 'language', content: locale.value, key: 'language'
    },

    // 地区相关元数据
    { name: 'geo.region', content: 'US', key: 'geo.region' },
    { name: 'geo.placename', content: 'United States', key: 'geo.placename' },

    // 如果需要禁止索引
    {
      name: 'robots',
      content: currentSeoConfig.value.noIndex ? 'noindex, nofollow' : 'index, follow',
      key: 'robots'
    },
  ]),
  link: computed(() => [
    {
      rel: 'sitemap',
      type: 'application/xml',
      href: sitemapUrl,
      key: 'sitemap-xml',
    },
    // 规范链接
    { rel: 'canonical', href: canonicalUrl.value, key: 'canonical' },
    // 替代语言链接
    ...generateAlternateLanguageLinks()
  ]),
  htmlAttrs: {
    lang: locale,
  }
});

// 生成替代语言链接
function generateAlternateLanguageLinks() {

  // 使用appStore.localeOptions获取支持的语言列表
  return appStore.localeOptions
    .filter((option: App.I18n.LangOption) => option.key !== locale.value) // 排除当前语言
    .map((option: App.I18n.LangOption) => {
      const langCode = option.key.split('-')[0];
      // 使用查询参数方式链接到其他语言版本
      const url = new URL(`${baseUrl}${route.path}`);
      url.searchParams.set('lang', option.key);

      return {
        rel: 'alternate',
        hreflang: langCode,
        href: url.toString(),
        key: `alternate-${langCode}`
      };
    });
}

// 监听语言变化，更新SEO内容
watch(
  () => appStore.locale,
  () => {
    // 当语言变化时，SEO计算属性会自动更新
    console.log(`Language changed to ${locale.value}, updating SEO data`);
  }
);

// 监听路由变化，在500错误页时更新状态码
let originalTitle = document.title;

onMounted(() => {
  originalTitle = document.title;
  updateStatusCodeMetaTag();
});

onUnmounted(() => {
  document.title = originalTitle;
  // 移除状态码meta标签
  const statusCodeMeta = document.querySelector('meta[name="status-code"]');
  if (statusCodeMeta) {
    statusCodeMeta.remove();
  }
});

// 监听路由变化以更新状态码
watch(
  () => route.path,
  () => {
    updateStatusCodeMetaTag();
  }
);

// 更新HTTP状态码meta标签（用于静态页面生成时的状态码）
function updateStatusCodeMetaTag() {
  const errorRoutes: Record<string, number> = {
    '/404': 404,
    '/403': 403,
    '/500': 500,
  };

  let statusCode = 200; // 默认状态码

  // 检查当前路由是否匹配错误路由
  if (route.path in errorRoutes) {
    statusCode = errorRoutes[route.path];
  }

  // 更新或创建状态码meta标签
  let statusCodeMeta = document.querySelector('meta[name="status-code"]');

  if (!statusCodeMeta) {
    statusCodeMeta = document.createElement('meta');
    statusCodeMeta.setAttribute('name', 'status-code');
    document.head.appendChild(statusCodeMeta);
  }

  statusCodeMeta.setAttribute('content', statusCode.toString());
}
</script>